import { useMutation, useQueryClient } from 'react-query';
import { predictionManagerAxiosApi } from 'src/Config/axiosApi';
import { SYSTEM_ALL_STATE_KEY } from 'src/Common/hooks/useSystemKillSwitch';

export type OrchestratorDomain = 'opener' | 'unwinder';

/**
 * `predictionManagerAxiosApi` is created with NO timeout. A hung boot would
 * otherwise leave `isLoading` true forever — the CTA stuck disabled, and past
 * the gate's own deadline the operator able to stack a second POST on top.
 */
export const ORCHESTRATOR_BOOT_TIMEOUT_MS = 30000;

const DOMAIN_STATE_KEY: Record<OrchestratorDomain, string> = {
  opener: 'opener-state',
  unwinder: 'unwinder-state'
};

/**
 * Boot / shut down a domain's orchestrator (control plane) pod.
 *
 * The knob lives on the MANAGER base (`predictionManagerAxiosApi`), NEVER on
 * the orchestrator it controls — a pod scaled to 0 cannot serve its own boot.
 *
 * Kept in its own tiny module (rather than in `useEntry` / `useUnwinding`) so
 * the boot CTA can be rendered from any screen without dragging a rule-CRUD
 * module graph along with it.
 */
export function useBootOrchestrator(domain: OrchestratorDomain) {
  const qc = useQueryClient();
  return useMutation(
    async () => {
      const { data } = await predictionManagerAxiosApi.post(
        `/api/system/${domain}-orchestrator/boot`,
        undefined,
        { timeout: ORCHESTRATOR_BOOT_TIMEOUT_MS }
      );
      return data;
    },
    {
      mutationKey: `${domain}:boot-orchestrator`,
      onSuccess: () => {
        qc.invalidateQueries([DOMAIN_STATE_KEY[domain]]);
        qc.invalidateQueries([SYSTEM_ALL_STATE_KEY]);
      }
    }
  );
}

/** Mirror of `useBootOrchestrator` — also on the MANAGER base. */
export function useShutdownOrchestrator(domain: OrchestratorDomain) {
  const qc = useQueryClient();
  return useMutation(
    async () => {
      const { data } = await predictionManagerAxiosApi.post(
        `/api/system/${domain}-orchestrator/shutdown`,
        undefined,
        { timeout: ORCHESTRATOR_BOOT_TIMEOUT_MS }
      );
      return data;
    },
    {
      mutationKey: `${domain}:shutdown-orchestrator`,
      onSuccess: () => {
        qc.invalidateQueries([DOMAIN_STATE_KEY[domain]]);
        qc.invalidateQueries([SYSTEM_ALL_STATE_KEY]);
      }
    }
  );
}
